import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createApiClient, type ApiClient } from "../../api/client";
import { useApiToken } from "../../auth/useApiToken";
import {
  chatApi,
  isCollectionComplete,
  START_COLLECTION,
  VALIDATE_AND_GENERATE,
  type BriefData,
  type ChatResponse,
  type LastQuestionDict,
} from "../../api/chat";

export interface Attachment {
  name: string;
  previewUrl?: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  attachments?: Attachment[];
}

export interface StagedFile {
  file: File;
  name: string;
  size: number;
  previewUrl?: string;
}

export interface LastQuestion {
  text?: string;
  id?: number | string | null;
  dict?: LastQuestionDict | null;
}

export type ConversationPhase =
  | "initializing"
  | "ready"
  | "sending"
  | "generating"
  | "success"
  | "error";

const isImageFile = (name: string): boolean => /\.(png|jpe?g|webp|gif)$/i.test(name);

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("read error"));
    reader.readAsDataURL(file);
  });
}

function buildSyntheticAnswer(attribut: string | undefined, names: string[]): string {
  const list = names.join(", ");
  if (attribut === "visuel_composition_visuelle") {
    return `J'ai partagé des fichiers de visuels finaux en pièce jointe. Fichiers: ${list}. Ils seront envoyés au stockage final au moment de la validation du brief.`;
  }
  if (attribut === "visuel_reference_url") {
    return `J'ai partagé des fichiers d'inspiration / de référence en pièce jointe. Fichiers: ${list}.`;
  }
  return `J'ai partagé des fichiers en pièce jointe. Fichiers: ${list}.`;
}

export interface UseConversation {
  threadId: string;
  messages: ChatMessage[];
  briefData: BriefData;
  lastQuestion: LastQuestion;
  waitingForValidation: boolean;
  generationSuccess: boolean;
  documentUrl: string | null;
  stagedFiles: StagedFile[];
  phase: ConversationPhase;
  error: string | null;
  /** Send a free-text answer. */
  send: (text: string) => Promise<void>;
  /** Send a predefined single/dropdown selection. */
  confirmChoice: (selection: string) => Promise<void>;
  /** Send a multi-select answer (joined with ", "). */
  confirmMulti: (selections: string[]) => Promise<void>;
  /** Send a date answer already formatted as YYYY-MM-DD. */
  confirmDate: (isoDate: string) => Promise<void>;
  /** Stage files locally (upload happens at generation). Returns count added. */
  addFiles: (files: File[]) => Promise<number>;
  removeFile: (index: number) => void;
  /** Send the staged files as the answer to the current image question. */
  submitImageAnswer: () => Promise<void>;
  /** Upload staged files then trigger final generation. */
  validateAndGenerate: () => Promise<void>;
  /** Leave the validation gate to send corrections. */
  modify: () => void;
  /** Start a brand-new brief (new thread). */
  reset: () => void;
}

export function useConversation(): UseConversation {
  const getToken = useApiToken();
  const client: ApiClient = useMemo(
    () => createApiClient({ baseUrl: import.meta.env.VITE_API_BASE_URL, getToken }),
    [getToken],
  );

  const [threadId, setThreadId] = useState<string>(() => crypto.randomUUID());
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [briefData, setBriefData] = useState<BriefData>({});
  const [lastQuestion, setLastQuestion] = useState<LastQuestion>({});
  const [waitingForValidation, setWaitingForValidation] = useState(false);
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [phase, setPhase] = useState<ConversationPhase>("initializing");
  const [error, setError] = useState<string | null>(null);

  // Guards: prevent double init (React 18 StrictMode double-invoke) and
  // overlapping in-flight turns.
  const initedRef = useRef(false);
  const busyRef = useRef(false);
  const stagedKeysRef = useRef<Set<string>>(new Set());

  const applyResponse = useCallback((res: ChatResponse) => {
    setMessages((prev) => [...prev, { role: "assistant", content: res.response ?? "" }]);
    if (res.brief_data) setBriefData(res.brief_data);
    setLastQuestion({
      text: res.last_question ?? undefined,
      id: res.last_question_id ?? null,
      dict: res.last_question_dictionnary ?? null,
    });
    setWaitingForValidation(isCollectionComplete(res));
  }, []);

  /**
   * Run one conversation turn. `userBubble` is appended to the transcript when
   * the input is a real user answer (not a hidden sentinel like START_COLLECTION).
   */
  const runTurn = useCallback(
    async (userInput: string, userBubble?: ChatMessage): Promise<void> => {
      if (busyRef.current) return;
      busyRef.current = true;
      setError(null);
      setPhase("sending");
      if (userBubble) setMessages((prev) => [...prev, userBubble]);
      try {
        const res = await chatApi.send(client, threadId, userInput);
        applyResponse(res);
        setPhase("ready");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setPhase("error");
      } finally {
        busyRef.current = false;
      }
    },
    [client, threadId, applyResponse],
  );

  // Auto-start collection on a fresh thread (iso Streamlit first-load).
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    void (async () => {
      busyRef.current = true;
      setPhase("initializing");
      try {
        const res = await chatApi.send(client, threadId, START_COLLECTION);
        applyResponse(res);
        setPhase("ready");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setPhase("error");
      } finally {
        busyRef.current = false;
      }
    })();
  }, [client, threadId, applyResponse]);

  const send = useCallback(
    (text: string) => runTurn(text, { role: "user", content: text }),
    [runTurn],
  );

  const confirmChoice = useCallback(
    (selection: string) => runTurn(selection, { role: "user", content: selection }),
    [runTurn],
  );

  const confirmMulti = useCallback(
    (selections: string[]) => {
      const joined = selections.join(", ");
      return runTurn(joined, { role: "user", content: joined });
    },
    [runTurn],
  );

  const confirmDate = useCallback(
    (isoDate: string) => runTurn(isoDate, { role: "user", content: isoDate }),
    [runTurn],
  );

  const addFiles = useCallback(async (files: File[]): Promise<number> => {
    const staged: StagedFile[] = [];
    for (const file of files) {
      const key = `${file.name}_${file.size}`;
      if (stagedKeysRef.current.has(key)) continue;
      stagedKeysRef.current.add(key);
      const previewUrl = isImageFile(file.name)
        ? await fileToDataUrl(file).catch(() => undefined)
        : undefined;
      staged.push({ file, name: file.name, size: file.size, previewUrl });
    }
    if (staged.length) setStagedFiles((prev) => [...prev, ...staged]);
    return staged.length;
  }, []);

  const removeFile = useCallback((index: number) => {
    setStagedFiles((prev) => {
      const target = prev[index];
      if (target) stagedKeysRef.current.delete(`${target.name}_${target.size}`);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const submitImageAnswer = useCallback(async () => {
    const attribut = lastQuestion.dict?.attributs?.[0];
    const names = stagedFiles.map((f) => f.name);
    if (names.length === 0) return;
    const attachments: Attachment[] = stagedFiles.map((f) => ({
      name: f.name,
      previewUrl: f.previewUrl,
    }));
    const synthetic = buildSyntheticAnswer(attribut, names);
    await runTurn(synthetic, {
      role: "user",
      content: "📎 Fichiers partagés",
      attachments,
    });
  }, [lastQuestion, stagedFiles, runTurn]);

  const validateAndGenerate = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setError(null);
    setPhase("generating");
    try {
      if (stagedFiles.length > 0) {
        const up = await chatApi.uploadFiles(
          client,
          threadId,
          stagedFiles.map((f) => f.file),
        );
        if (up.error) {
          setError(`Échec de l'envoi des fichiers : ${up.error}`);
          setPhase("ready");
          return;
        }
      }
      const res = await chatApi.send(client, threadId, VALIDATE_AND_GENERATE);
      const ok = res.status === "finished" || (res.response ?? "").startsWith("✅");
      if (ok) {
        if (res.brief_data) setBriefData(res.brief_data);
        setDocumentUrl(res.document_url ?? null);
        setGenerationSuccess(true);
        setStagedFiles([]);
        stagedKeysRef.current.clear();
        setPhase("success");
      } else {
        // Generation refused/incomplete: surface the assistant message and stay
        // in the validation gate (iso Streamlit).
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: res.response || "Une erreur est survenue." },
        ]);
        if (res.brief_data) setBriefData(res.brief_data);
        setPhase("ready");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase("error");
    } finally {
      busyRef.current = false;
    }
  }, [client, threadId, stagedFiles]);

  const modify = useCallback(() => {
    setWaitingForValidation(false);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "D'accord, je vous écoute. Quelle correction ou précision souhaitez-vous apporter au brief ?",
      },
    ]);
  }, []);

  const reset = useCallback(() => {
    initedRef.current = false;
    busyRef.current = false;
    stagedKeysRef.current = new Set();
    setMessages([]);
    setBriefData({});
    setLastQuestion({});
    setWaitingForValidation(false);
    setGenerationSuccess(false);
    setDocumentUrl(null);
    setStagedFiles([]);
    setError(null);
    setPhase("initializing");
    setThreadId(crypto.randomUUID()); // triggers the init effect for the new thread
  }, []);

  return {
    threadId,
    messages,
    briefData,
    lastQuestion,
    waitingForValidation,
    generationSuccess,
    documentUrl,
    stagedFiles,
    phase,
    error,
    send,
    confirmChoice,
    confirmMulti,
    confirmDate,
    addFiles,
    removeFile,
    submitImageAnswer,
    validateAndGenerate,
    modify,
    reset,
  };
}
